import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BookingResponse, BookingResponseData } from '../../../../services/housekeeping-forecast-report/response/BookingResponse';

interface HousekeepingForecastTableReportProps {
    data: BookingResponse;
}

const HousekeepingForecastTableReport: React.FC<HousekeepingForecastTableReportProps> = ({ data }) => {
    const columnsReservations = [
        { label: "Date", key: "date" },
        { label: "Total Room", key: "total_room" },
        { label: "Room Reserved", key: "room_reserverd" },
        { label: "Occupancy", key: "occupancy" },
        { label: "Arrival Rooms", key: "arrival_rooms" },
        { label: "In-House Rooms", key: "in_house_rooms" },
        { label: "Departure Rooms", key: "departure_rooms" },
        { label: "Adults In-House", key: "adult_in_house" },
        { label: "Children In-House", key: "children_in_house" },
        { label: "Cancelled Room", key: "cancelled_room" },
        { label: "Maintenance", key: "maintenance" },
    ];

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Housekeeping Forecast Report", 14, 10);

        const tableData = data.data.map(row => columnsReservations.map(col => {
            if (col.key === 'rates' || col.key === 'room_only') {
                return formatPrice(Number(row[col.key as keyof BookingResponseData]));
            }
            return row[col.key as keyof BookingResponseData] || '-';
        }));

        autoTable(doc, {
            head: [columnsReservations.map(col => col.label)],
            body: tableData,
        });

        doc.save("HousekeepingForecastReport.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columnsReservations.map(col => col.label),
            ...data.data.map(row => columnsReservations.map(col => {
                if (col.key === 'rates' || col.key === 'room_only') {
                    return formatPrice(Number(row[col.key as keyof BookingResponseData]));
                }
                return row[col.key as keyof BookingResponseData] || '-';
            }))
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "HousekeepingForecastReport.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsReservations.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.data.length > 0 ? (
                            [...data.data]
                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                .map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {columnsReservations.map((col, colIndex) => (
                                            <td key={colIndex} className='text-center'>
                                                {(() => {
                                                    const value = row[col.key as keyof BookingResponseData] || '-';

                                                    const badgeMap: Record<string, string> = {
                                                        arrival_rooms: 'primary',
                                                        in_house_rooms: 'success',
                                                        departure_rooms: 'warning',
                                                        cancelled_room: 'danger',
                                                        maintenance: 'secondary',
                                                    };

                                                    if (badgeMap[col.key]) {
                                                        return <span className={`badge bg-${badgeMap[col.key]} text-white`}>{value}</span>;
                                                    }

                                                    return value;
                                                })()}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan={columnsReservations.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default HousekeepingForecastTableReport;